/**
 * useAttendeesAndGroups Hook
 * React Query hook for fetching calendar attendees (users, contacts, groups)
 * with dynamic case filtering
 */

import { useQuery } from '@tanstack/react-query';
import { getCalendarAttendees } from 'services/users/userServices';
import { generateFullName } from 'utils/generateFullNameUtils';

/**
 * Fetch and format attendees and groups
 * @param {string|null} caseId - Case ID for filtering (null for global calendar)
 * @param {boolean} enabled - Whether the query should run
 * @returns {object} React Query result with formatted user list
 */
export const useAttendeesAndGroups = (caseId = null, enabled = true) => {
  return useQuery({
    queryKey: ['calendar-attendees', caseId],
    queryFn: async () => {
      const params = {
        caseId: caseId || null,
        is_active: true,
        active_groups: true,
      };

      const response = await getCalendarAttendees(params);

      if (!response?.data) {
        return [];
      }

      // Group attendees by type
      const groupedContacts = {};

      response.data.forEach((element) => {
        const contactType = element?.res_type === 'user-group' ? 'Groups' : !element.contact_type ? 'Firm Users' : 'Contacts';
        const item = {
          ...element,
          label: generateFullName(element),
          value: element?.firm_user_id || element?.contact_id || element?.group_id,
        };

        if (!groupedContacts[contactType]) {
          groupedContacts[contactType] = { label: contactType, items: [] };
        }
        groupedContacts[contactType].items.push(item);
      });

      // Sort each group's items by first_name
      Object.values(groupedContacts).forEach((group) => {
        group.items.sort((a, b) => {
          const firstNameA = (a?.first_name || a?.group_name || '').toLowerCase();
          const firstNameB = (b?.first_name || b?.group_name || '').toLowerCase();
          return firstNameA.localeCompare(firstNameB);
        });
      });

      return Object.values(groupedContacts);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
    refetchOnWindowFocus: false,
    retry: 1,
  });
};
